import { getDaysInMonth } from "date-fns";
import { DAY_TYPES, ATT_STATUS, getAttendanceStatus, LEGACY_WORK_CONFIG, countPaidDays } from "../firebase/schema";

// Bảng chấm công dạng ma trận: mỗi promoter một dòng, mỗi ngày trong tháng một cột.
// Ô ngày đi làm hiện số giờ; ngày nghỉ hiện ký hiệu (OFF / OL / P / LỄ) kèm màu nền.

// Màu lấy thẳng từ ATT_STATUS để bảng trên web và file Excel không thể lệch nhau
const argb = (hex) => "FF" + hex.replace("#", "");
const FILL = {
  header: "FFC8152A",   // đỏ MSI
  weekend: "FFF2F2F2",  // nền cột T7/CN
};
const STATUS_FILL = Object.fromEntries(
  Object.entries(ATT_STATUS).map(([k, v]) => [k, v.color ? argb(v.color) : null])
);
// Ký hiệu chữ trong ô cho các trạng thái không hiện số giờ
const STATUS_TEXT = {
  holiday: "LỄ", annualLeave: "PN", unpaidLeave: "KP", weeklyOff: "OFF", missing: "?", future: "",
};

const DOW_VI = ["CN", "T2", "T3", "T4", "T5", "T6", "T7"];

const thin = { style: "thin", color: { argb: "FFBFBFBF" } };
const baseBorder = { top: thin, left: thin, bottom: thin, right: thin };

const solid = (argb) => ({ type: "pattern", pattern: "solid", fgColor: { argb } });

/**
 * Phân loại một ô ngày → { text, fill }
 * Dùng lại đúng quy tắc "đủ công" của bảng ma trận trên web (getCellClass).
 */
function classifyDay({ rec, dateStr, todayStr, isHoliday, work }) {
  const st = getAttendanceStatus({ rec, dateStr, todayStr, isHoliday, work });
  const fill = STATUS_FILL[st];
  // Ngày đi làm (đủ công / thiếu giờ / lễ có đi làm) hiện số giờ; còn lại hiện ký hiệu
  const hours = Number(rec?.totalHours) || 0;
  if ((st === "full" || st === "short" || st === "holiday") && rec && hours > 0) {
    return { text: hours, fill, pending: rec.leaveStatus === "pending" };
  }
  return { text: STATUS_TEXT[st] ?? "", fill, pending: rec?.leaveStatus === "pending" };
}

export async function exportAttendanceMatrixXLSX({ promoters, month, records, holidays = [], overtime = [], work = LEGACY_WORK_CONFIG }) {
  const ExcelJS = (await import("exceljs")).default;

  const [year, mon] = month.split("-").map(Number);
  const daysInMonth = getDaysInMonth(new Date(year, mon - 1, 1));
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const todayStr = new Date().toISOString().slice(0, 10);
  const holidaySet = new Set(holidays.map(h => h.date));

  // recordMap[promoterId][date] = bản ghi chấm công
  const recordMap = {};
  records.forEach(r => {
    if (!recordMap[r.promoterId]) recordMap[r.promoterId] = {};
    recordMap[r.promoterId][r.date] = r;
  });

  const wb = new ExcelJS.Workbook();
  wb.creator = "PromoterHQ";
  wb.created = new Date();
  const ws = wb.addWorksheet(`Cham cong ${month}`, {
    views: [{ state: "frozen", xSplit: 3, ySplit: 2 }],
  });

  const firstDayCol = 4;                       // cột D
  const lastDayCol = firstDayCol + daysInMonth - 1;
  const colWorkDays = lastDayCol + 1;
  const colAnnual = lastDayCol + 2;

  // ── Hàng 1: tiêu đề + số ngày ──
  const head1 = ["Year", "Month", "Promoter Name", ...days, "Số công", "Phép năm"];
  // ── Hàng 2: thứ trong tuần ──
  const head2 = ["", "", "", ...days.map(d => DOW_VI[new Date(year, mon - 1, d).getDay()]), "", ""];
  ws.addRow(head1);
  ws.addRow(head2);

  [1, 2].forEach(rowNum => {
    const row = ws.getRow(rowNum);
    row.height = rowNum === 1 ? 20 : 16;
    row.eachCell({ includeEmpty: true }, cell => {
      cell.fill = solid(FILL.header);
      cell.font = { bold: true, size: rowNum === 1 ? 11 : 9, color: { argb: "FFFFFFFF" } };
      cell.alignment = { horizontal: "center", vertical: "middle" };
      cell.border = { ...baseBorder };
    });
    row.getCell(3).alignment = { horizontal: "left", vertical: "middle" };
  });

  // ── Một dòng cho mỗi promoter ──
  promoters.forEach(p => {
    const pMap = recordMap[p.uid] || {};
    const values = [year, `M${String(mon).padStart(2, "0")}`, p.name];

    days.forEach(d => {
      const dateStr = `${month}-${String(d).padStart(2, "0")}`;
      const { text } = classifyDay({
        rec: pMap[dateStr], dateStr, todayStr, isHoliday: holidaySet.has(dateStr), work,
      });
      values.push(text);
    });

    const recs = Object.values(pMap);
    const workDays = countPaidDays({ records: recs, holidays, month, startDate: p.startDate, work,
      overtime: overtime.filter(o => o.promoterId === p.uid) });
    const annualDays = recs.filter(r => r.dayType === DAY_TYPES.ANNUAL_LEAVE).length;
    values.push(workDays, annualDays);

    const row = ws.addRow(values);
    row.height = 18;

    row.eachCell({ includeEmpty: true }, (cell, colNumber) => {
      cell.border = { ...baseBorder };
      cell.font = { size: 10 };
      cell.alignment = { horizontal: colNumber === 3 ? "left" : "center", vertical: "middle" };

      if (colNumber >= firstDayCol && colNumber <= lastDayCol) {
        const d = colNumber - firstDayCol + 1;
        const dateStr = `${month}-${String(d).padStart(2, "0")}`;
        const dow = new Date(year, mon - 1, d).getDay();
        const { fill } = classifyDay({
          rec: pMap[dateStr], dateStr, todayStr, isHoliday: holidaySet.has(dateStr), work,
        });
        if (fill) cell.fill = solid(fill);
        else if (dow === 0 || dow === 6) cell.fill = solid(FILL.weekend);
        // Vạch đậm cuối mỗi tuần (sau Chủ nhật)
        if (dow === 0) cell.border = { ...baseBorder, right: { style: "medium", color: { argb: "FF808080" } } };
      }

      if (colNumber === colWorkDays || colNumber === colAnnual) {
        cell.font = { size: 10, bold: true };
        cell.fill = solid("FFFAFAFA");
      }
    });
  });

  // Vạch đậm cuối tuần cho cả 2 hàng header
  days.forEach(d => {
    const dow = new Date(year, mon - 1, d).getDay();
    if (dow !== 0) return;
    [1, 2].forEach(rowNum => {
      const cell = ws.getRow(rowNum).getCell(firstDayCol + d - 1);
      cell.border = { ...baseBorder, right: { style: "medium", color: { argb: "FF808080" } } };
    });
  });

  // ── Độ rộng cột ──
  ws.getColumn(1).width = 7;
  ws.getColumn(2).width = 8;
  ws.getColumn(3).width = 24;
  for (let c = firstDayCol; c <= lastDayCol; c++) ws.getColumn(c).width = 4.5;
  ws.getColumn(colWorkDays).width = 9;
  ws.getColumn(colAnnual).width = 10;

  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: colAnnual } };

  // ── Chú thích màu ──
  const legendRow = ws.rowCount + 2;
  ws.getCell(legendRow, 1).value = "Chú thích:";
  ws.getCell(legendRow, 1).font = { bold: true, size: 10 };
  const legend = [
    ["8", STATUS_FILL.full, "Đủ công (làm đủ giờ, nghỉ có lương, hoặc admin xác nhận)"],
    ["?", STATUS_FILL.missing, "Thiếu check-in hoặc check-out — được giải trình"],
    ["6", STATUS_FILL.short, "Đi làm nhưng thiếu giờ — được giải trình"],
    ["LỄ", STATUS_FILL.holiday, "Ngày lễ (hệ số ×3)"],
    ["PN", STATUS_FILL.annualLeave, "Nghỉ phép năm"],
    ["KP", STATUS_FILL.unpaidLeave, "Nghỉ không phép"],
    ["OFF", STATUS_FILL.weeklyOff, "Nghỉ tuần"],
  ];
  legend.forEach(([label, fill, desc], i) => {
    const r = legendRow + 1 + i;
    const c1 = ws.getCell(r, 1);
    c1.value = label;
    c1.alignment = { horizontal: "center" };
    c1.border = { ...baseBorder };
    if (fill) c1.fill = solid(fill);
    ws.getCell(r, 2).value = desc;
    ws.getCell(r, 2).font = { size: 10 };
  });

  // ── Tải file ──
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `cham-cong_${month}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}
